import React, { useState, useEffect, useRef } from 'react';
import { 
    LayoutDashboard, Filter, ShieldCheck, Database, RefreshCw, 
    GripVertical, Plus, Search, Trash2, Edit2, Play, Check, 
    HelpCircle, X, ChevronRight, Info, CreditCard, ExternalLink 
} from 'lucide-react';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('data-quality');
    const [portalId, setPortalId] = useState('');
    
    const [isLoading, setIsLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [stats, setStats] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncError, setSyncError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Config states
    const [initialConfig, setInitialConfig] = useState(null);
    const [activeFilters, setActiveFilters] = useState([]);
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [activityLogging, setActivityLogging] = useState(false);
    const [showApproxPins, setShowApproxPins] = useState(true);
    const [pulsePinsEnabled, setPulsePinsEnabled] = useState(true);
    const [pulseOnlyActiveDeals, setPulseOnlyActiveDeals] = useState(false);
    const [autoUserAccess, setAutoUserAccess] = useState(true);
    const [userRoles, setUserRoles] = useState({});
    const [userActivity, setUserActivity] = useState({});
    const [filterPresetsEnabled, setFilterPresetsEnabled] = useState(true);
    const [filterPresets, setFilterPresets] = useState([]);
    const [billingInvoiceEmail, setBillingInvoiceEmail] = useState('');
    const [billingVatId, setBillingVatId] = useState('');

    // UI States
    const [properties, setProperties] = useState([]);
    const [users, setUsers] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [showAddFilter, setShowAddFilter] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [userSortField, setUserSortField] = useState('name');
    const [userSortDirection, setUserSortDirection] = useState('asc');
    
    // Modal states
    const [videoModal, setVideoModal] = useState({ open: false, tag: '', title: '', desc: '', key: '' });
    
    // Preset Edit State
    const [editingPreset, setEditingPreset] = useState(null);
    const [presetNameInput, setPresetNameInput] = useState('');
    const [presetRules, setPresetRules] = useState([]);
    const [isRematching, setIsRematching] = useState(null); // 'failed' | 'all' | null

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleRematch = async (type) => {
        setIsRematching(type);
        try {
            const res = await fetch('/api/onboarding/rematch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ portalId, type })
            });
            const data = await res.json();
            if (data.success) {
                showToast(type === 'failed' 
                    ? `${data.count} fehlgeschlagene Standorte erfolgreich zurückgesetzt.` 
                    : `${data.count} Standorte erfolgreich zurückgesetzt.`, 'success');
                // Reload stats
                const statsRes = await fetch(`/api/onboarding/stats?portalId=${portalId}`);
                const statsData = await statsRes.json();
                if (statsData.success) setStats(statsData.stats);
            } else {
                showToast('Fehler: ' + (data.message || 'Rematch fehlgeschlagen'), 'error');
            }
        } catch (e) {
            showToast('Netzwerkfehler beim Zurücksetzen der Daten', 'error');
        } finally {
            setIsRematching(null);
        }
    };

    const handleTogglePulsePins = () => {
        const newVal = !pulsePinsEnabled;
        setPulsePinsEnabled(newVal);
        if (newVal) {
            setPulseOnlyActiveDeals(false);
        }
    };

    const handleTogglePulseOnlyActiveDeals = () => {
        const newVal = !pulseOnlyActiveDeals;
        setPulseOnlyActiveDeals(newVal);
        if (newVal) {
            setPulsePinsEnabled(false);
        }
    };



    const loadInitialData = async (pid) => {
        setIsLoading(true);
        try {
            // Fetch configuration
            const confRes = await fetch(`/api/admin/config?portalId=${pid}`);
            const confData = await confRes.json();
            
            // Fetch properties
            const propRes = await fetch(`/api/admin/properties?portalId=${pid}`);
            const propData = await propRes.json();
            const fetchedProps = propData.success ? propData.properties : [];
            setProperties(fetchedProps);

            if (confData.success && confData.config) {
                const conf = confData.config;
                setInitialConfig(conf);
                
                // Map properties
                let savedFilters = conf.active_filters || [];
                if (savedFilters.length === 0) {
                    savedFilters = ["annualrevenue", "numberofemployees", "hubspot_owner_id", "hubspot_team_id", "hs_num_open_deals", "num_associated_deals"];
                }
                savedFilters = savedFilters.map(f => {
                    if (typeof f === 'string') {
                        return fetchedProps.find(p => p.name === f) || { name: f, label: f, type: 'string' };
                    }
                    return f;
                });
                
                setActiveFilters(savedFilters);
                setAllowedUsers(conf.allowed_users || []);
                setActivityLogging(conf.activity_logging_enabled || false);
                setShowApproxPins(conf.show_approx_pins !== undefined ? conf.show_approx_pins : true);
                setPulsePinsEnabled(conf.pulse_pins_enabled !== undefined ? conf.pulse_pins_enabled : true);
                setPulseOnlyActiveDeals(conf.pulse_only_active_deals !== undefined ? conf.pulse_only_active_deals : false);
                setAutoUserAccess(conf.auto_user_access !== undefined ? conf.auto_user_access : true);
                setUserRoles(conf.user_roles || {});
                setUserActivity(conf.user_activity || {});
                setFilterPresetsEnabled(conf.filter_presets_enabled !== undefined ? conf.filter_presets_enabled : true);
                setFilterPresets(conf.filter_presets || [
                    { id: '1', name: 'Tech-Unternehmen NRW', desc: 'Industry = Software · State = NRW · Has open deals' },
                    { id: '2', name: 'Bestandskunden Süd', desc: 'Lifecycle = Customer · Country = DE · PLZ beginnt mit 7,8,9' },
                    { id: '3', name: 'Cold Leads für Acquisition', desc: 'Lifecycle = Lead · Last contact > 90d · Annual Revenue > 1M€' },
                    { id: '4', name: 'Roadshow Q2', desc: 'Owner = Marco · Has open deals · Annual Revenue zwischen 500k–5M€' }
                ]);
                setBillingInvoiceEmail(conf.billing_invoice_email || 'buchhaltung@acme.de');
                setBillingVatId(conf.billing_vat_id || 'DE298471293');
            }

            // Fetch geocoding stats
            const statsRes = await fetch(`/api/onboarding/stats?portalId=${pid}`);
            const statsData = await statsRes.json();
            if (statsData.success) setStats(statsData.stats);

            // Fetch HubSpot users/owners
            const usersRes = await fetch(`/api/admin/users?portalId=${pid}`);
            const usersData = await usersRes.json();
            if (usersData.success) setUsers(usersData.users);

        } catch (e) {
            console.error('Failed loading configuration data', e);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const pid = params.get('portalId') || '';
        setPortalId(pid);
        loadInitialData(pid);
    }, []);

    // Check if configuration has modifications (isDirty)
    const isDirty = () => {
        if (!initialConfig) return false;
        
        const currentConfig = {
            active_filters: activeFilters.map(f => f.name),
            allowed_users: allowedUsers,
            activity_logging_enabled: activityLogging,
            show_approx_pins: showApproxPins,
            pulse_pins_enabled: pulsePinsEnabled,
            pulse_only_active_deals: pulseOnlyActiveDeals,
            auto_user_access: autoUserAccess,
            user_roles: userRoles,
            filter_presets_enabled: filterPresetsEnabled,
            filter_presets: filterPresets,
            billing_invoice_email: billingInvoiceEmail,
            billing_vat_id: billingVatId
        };

        const initCompare = {
            active_filters: (initialConfig.active_filters || []).map(f => typeof f === 'string' ? f : f.name),
            allowed_users: initialConfig.allowed_users || [],
            activity_logging_enabled: initialConfig.activity_logging_enabled || false,
            show_approx_pins: initialConfig.show_approx_pins !== undefined ? initialConfig.show_approx_pins : true,
            pulse_pins_enabled: initialConfig.pulse_pins_enabled !== undefined ? initialConfig.pulse_pins_enabled : true,
            pulse_only_active_deals: initialConfig.pulse_only_active_deals !== undefined ? initialConfig.pulse_only_active_deals : false,
            auto_user_access: initialConfig.auto_user_access !== undefined ? initialConfig.auto_user_access : true,
            user_roles: initialConfig.user_roles || {},
            filter_presets_enabled: initialConfig.filter_presets_enabled !== undefined ? initialConfig.filter_presets_enabled : true,
            filter_presets: initialConfig.filter_presets || [],
            billing_invoice_email: initialConfig.billing_invoice_email || 'buchhaltung@acme.de',
            billing_vat_id: initialConfig.billing_vat_id || 'DE298471293'
        };

        return JSON.stringify(currentConfig) !== JSON.stringify(initCompare);
    };

    const saveConfig = async () => {
        setIsSaving(true);
        try {
            const res = await fetch('/api/admin/config', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portalId,
                    activeFilters,
                    allowedUsers,
                    activityLogging,
                    showApproxPins,
                    pulsePinsEnabled,
                    pulseOnlyActiveDeals,
                    autoUserAccess,
                    userRoles,
                    filterPresetsEnabled,
                    filterPresets,
                    billingInvoiceEmail,
                    billingVatId
                })
            });
            const d = await res.json();
            if (d.success) {
                // Update initialConfig so isDirty becomes false
                setInitialConfig({
                    active_filters: activeFilters,
                    allowed_users: allowedUsers,
                    activity_logging_enabled: activityLogging,
                    show_approx_pins: showApproxPins,
                    pulse_pins_enabled: pulsePinsEnabled,
                    pulse_only_active_deals: pulseOnlyActiveDeals,
                    auto_user_access: autoUserAccess,
                    user_roles: userRoles,
                    filter_presets_enabled: filterPresetsEnabled,
                    filter_presets: filterPresets,
                    billing_invoice_email: billingInvoiceEmail,
                    billing_vat_id: billingVatId
                });
            } else {
                showToast(`Fehler beim automatischen Speichern: ${d.message || 'Unbekannter Fehler'}`, 'error');
            }
        } catch(e) {
            showToast(`Netzwerkfehler beim automatischen Speichern: ${e.message}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Auto-save configuration changes
    useEffect(() => {
        if (!initialConfig || isLoading) return;

        if (isDirty()) {
            const timer = setTimeout(() => {
                saveConfig();
            }, 800); // 800ms debounce
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeFilters,
        allowedUsers,
        activityLogging,
        showApproxPins,
        pulsePinsEnabled,
        pulseOnlyActiveDeals,
        autoUserAccess,
        userRoles,
        filterPresetsEnabled,
        filterPresets,
        billingInvoiceEmail,
        billingVatId
    ]);

    const triggerSync = async () => {
        setIsSyncing(true);
        setSyncError('');
        let isDone = false;
        let afterCursor = null;
        while (!isDone) {
            try {
                const r = await fetch('/api/onboarding/process-batch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ portalId, after: afterCursor })
                });
                const d = await r.json();
                if (d.success) {
                    if (!d.after) {
                        isDone = true;
                    }
                    afterCursor = d.after || null;
                    
                    // Update stats locally to avoid hammering HubSpot search API
                    setStats(prev => prev ? {
                        ...prev,
                        pending: Math.max(0, prev.pending - d.processed)
                    } : null);
                } else {
                    setSyncError(d.message);
                    isDone = true;
                }
            } catch(e) {
                setSyncError("Netzwerkfehler");
                isDone = true;
            }
            if (!isDone) {
                await new Promise(resolve => setTimeout(resolve, 800)); // 800ms throttle between batches
            }
        }
        // Fetch final stats when done to verify and update UI with exact figures
        try {
            const statsRes = await fetch(`/api/onboarding/stats?portalId=${portalId}`);
            const statsData = await statsRes.json();
            if (statsData.success) setStats(statsData.stats);
        } catch(e) {}
        setIsSyncing(false);
    };

    const handleSort = (field) => {
        if (userSortField === field) {
            setUserSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setUserSortField(field);
            if (field === 'lastActive' || field === 'usage') {
                setUserSortDirection('desc');
            } else {
                setUserSortDirection('asc');
            }
        }
    };

    // User white list / role management
    const handleRoleChange = (userId, role) => {
        const idStr = String(userId);
        
        // Update Roles
        setUserRoles(prev => ({
            ...prev,
            [idStr]: role
        }));
        
        // Sync Whitelist (if role is user or admin, they are allowed. if none, they are blocked)
        if (role === 'none') {
            setAllowedUsers(prev => prev.filter(u => u !== idStr));
        } else {
            if (!allowedUsers.includes(idStr)) {
                setAllowedUsers(prev => [...prev, idStr]);
            }
        }
    };

    // Drag and Drop filter properties
    const handleDragStart = (idx) => {
        setDraggedIndex(idx);
    };

    const handleDragEnter = (targetIdx) => {
        if (draggedIndex === null || draggedIndex === targetIdx) return;
        setActiveFilters(prev => {
            const arr = [...prev];
            const [moved] = arr.splice(draggedIndex, 1);
            arr.splice(targetIdx, 0, moved);
            return arr;
        });
        setDraggedIndex(targetIdx);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const removeFilter = (propName) => {
        setActiveFilters(prev => prev.filter(f => f.name !== propName));
    };

    const addFilter = (prop) => {
        if (activeFilters.some(f => f.name === prop.name)) return;
        if (activeFilters.length >= 7) {
            showToast('Maximal 7 Filter erlaubt.', 'error');
            return;
        }
        setActiveFilters(prev => [...prev, prop]);
        setShowAddFilter(false);
        setFilterSearch('');
    };

    // Preset helpers
    const generateDescFromRules = (rules) => {
        if (!rules || rules.length === 0) return 'Keine Filter definiert';
        return rules.map(rule => {
            const prop = properties.find(p => p.name === rule.property) || { label: rule.property };
            const label = prop.label || rule.property;
            
            let opText = '';
            switch (rule.operator) {
                case 'EQ': opText = '='; break;
                case 'NEQ': opText = '!='; break;
                case 'CONTAINS': opText = 'enthält'; break;
                case 'GT': opText = '>'; break;
                case 'LT': opText = '<'; break;
                case 'HAS_PROPERTY': opText = 'bekannt'; break;
                case 'NOT_HAS_PROPERTY': opText = 'unbekannt'; break;
                default: opText = rule.operator;
            }
            
            if (rule.operator === 'HAS_PROPERTY' || rule.operator === 'NOT_HAS_PROPERTY') {
                return `${label} (${opText})`;
            }
            
            let valText = rule.value;
            if (prop.options && Array.isArray(prop.options)) {
                const foundOpt = prop.options.find(o => String(o.value) === String(rule.value));
                if (foundOpt) valText = foundOpt.label;
            }
            
            return `${label} ${opText} "${valText}"`;
        }).join(' · ');
    };

    const addPresetRule = () => {
        const defaultProp = properties[0];
        if (!defaultProp) return;
        const defaultOp = defaultProp.type === 'number' ? 'GT' : 'EQ';
        setPresetRules(prev => [...prev, { property: defaultProp.name, operator: defaultOp, value: '' }]);
    };

    const updatePresetRule = (index, updatedRule) => {
        setPresetRules(prev => prev.map((r, i) => i === index ? updatedRule : r));
    };

    const deletePresetRule = (index) => {
        setPresetRules(prev => prev.filter((_, i) => i !== index));
    };

    // Preset functions
    const handleAddOrEditPreset = () => {
        if (!presetNameInput.trim()) return;
        const autoDesc = generateDescFromRules(presetRules);
        
        if (editingPreset) {
            // Edit Mode
            setFilterPresets(prev => prev.map(p => p.id === editingPreset.id ? { 
                ...p, 
                name: presetNameInput, 
                desc: autoDesc,
                rules: presetRules
            } : p));
            setEditingPreset(null);
        } else {
            // Add Mode
            const newPreset = {
                id: String(Date.now()),
                name: presetNameInput,
                desc: autoDesc,
                rules: presetRules
            };
            setFilterPresets(prev => [...prev, newPreset]);
        }
        setPresetNameInput('');
        setPresetRules([]);
    };

    const deletePreset = (id) => {
        setFilterPresets(prev => prev.filter(p => p.id !== id));
    };

    // Donut math
    const totalCount = stats ? stats.total || 0 : 0;
    const exactPct = totalCount ? Math.round(((stats.exact || 0) / totalCount) * 100) : 0;
    const approxPct = totalCount ? Math.round(((stats.approx || 0) / totalCount) * 100) : 0;
    const failedPct = totalCount ? Math.round(((stats.failed || 0) / totalCount) * 100) : 0;
    const pendingPct = totalCount ? Math.round(((stats.pending || 0) / totalCount) * 100) : 0;

    const circ = 2 * Math.PI * 38; // 238.76
    const exactLength = (exactPct / 100) * circ;
    const approxLength = (approxPct / 100) * circ;
    const failedLength = (failedPct / 100) * circ;
    const pendingLength = (pendingPct / 100) * circ;

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%', background: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                <div style={{ textAlign: 'center' }}>
                    <div className="loader" style={{ margin: '0 auto 16px' }}></div>
                    <div style={{ fontSize: '15px', color: '#6B6F7A', fontWeight: '500' }}>Lade DealPin Setup...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-container">
            {toast && (
                <div style={{ position: 'fixed', top: '24px', left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? '#ff3b30' : (toast.type === 'info' ? '#1E62D8' : '#16A34A'), color: '#fff', padding: '12px 24px', borderRadius: '30px', fontWeight: '600', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 1000, transition: 'all 0.3s ease', fontFamily: 'Inter, sans-serif', fontSize: '13.5px' }}>
                    {toast.msg}
                </div>
            )}

            {/* ============ SIDEBAR ============ */}
            <aside className="sidebar">
                <div className="brand" style={{ padding: '0 22px 22px', borderBottom: '1px solid var(--sidebar-border)', marginBottom: '10px' }}>
                    <img src="/Deal-Pin-Logo-Long.png" alt="DealPin Logo" style={{ height: '32px', maxWidth: '100%', objectFit: 'contain' }} />
                </div>

                <div className="nav-section">
                    <div className="nav-label">Workspace</div>
                    
                    <button className={`nav-item ${activeTab === 'data-quality' ? 'active' : ''}`} onClick={() => setActiveTab('data-quality')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9"/>
                            <path d="M12 3 A 9 9 0 0 1 21 12 L 12 12 Z" fill="currentColor" stroke="none" opacity="0.18"/>
                            <path d="M12 12 L 21 12"/>
                            <path d="M12 12 L 12 3"/>
                        </svg>
                        <span>Datenqualität</span>
                    </button>

                    <button className={`nav-item ${activeTab === 'filters' ? 'active' : ''}`} onClick={() => setActiveTab('filters')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 5 L20 5 L14 12.5 L14 19 L10 21 L10 12.5 Z"/>
                        </svg>
                        <span>Suchfilter</span>
                    </button>

                    <button className={`nav-item ${activeTab === 'appearance' ? 'active' : ''}`} onClick={() => setActiveTab('appearance')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9"/>
                            <circle cx="12" cy="7.5" r="1.2" fill="currentColor" stroke="none"/>
                            <circle cx="16.5" cy="12" r="1.2" fill="currentColor" stroke="none"/>
                            <circle cx="14.5" cy="16" r="1.2" fill="currentColor" stroke="none"/>
                            <circle cx="8" cy="14" r="1.2" fill="currentColor" stroke="none"/>
                        </svg>
                        <span>Appearance</span>
                    </button>
                </div>

                <div className="nav-section">
                    <div className="nav-label">Account</div>
                    
                    <button className={`nav-item ${activeTab === 'access' ? 'active' : ''}`} onClick={() => setActiveTab('access')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="8" r="3.5"/>
                            <path d="M5 20 C 5 16, 8 14, 12 14 C 16 14, 19 16, 19 20"/>
                        </svg>
                        <span>Zugriff</span>
                    </button>

                </div>

            </aside>

            {/* ============ MAIN ============ */}
            <main className="main">
                <div className="topbar">
                    <div className="breadcrumb">
                        <span>Settings</span>
                        <span className="breadcrumb-sep">/</span>
                        <span className="breadcrumb-current">
                            {activeTab === 'data-quality' && 'Datenqualität'}
                            {activeTab === 'filters' && 'Suchfilter'}
                            {activeTab === 'appearance' && 'Appearance'}
                            {activeTab === 'access' && 'Zugriff'}
                        </span>
                    </div>
                    <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '32px' }}>
                        {isSaving ? (
                            <span style={{ fontSize: '13px', color: 'var(--ink-mute)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <RefreshCw size={14} className="spin" style={{ color: '#007AFF' }} />
                                Wird gespeichert...
                            </span>
                        ) : isDirty() ? (
                            <span style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
                                Änderungen ausstehend...
                            </span>
                        ) : (
                            <span style={{ fontSize: '13px', color: '#34C759', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Check size={14} />
                                In HubSpot gespeichert
                            </span>
                        )}
                    </div>
                </div>

                <div className="content">
                    {/* ============ TAB: DATENQUALITÄT ============ */}
                    {activeTab === 'data-quality' && (
                        <section className="section active">
                            <div className="page-header">
                                <div className="eyebrow">Geocoding-Qualität</div>
                                <h1 className="page-title">Datenqualität</h1>
                                <p className="page-lead">Wie präzise sind deine Company-Standorte? DealPin unterscheidet zwischen exakter Adresse, ungefährer Lage und nicht geokodierbaren Records.</p>
                            </div>

                            <div className="setting-group">
                                <div className="group-title">Verteilung nach Genauigkeit</div>

                                <div className="quality-grid">
                                    <div className="donut">
                                        <svg viewBox="0 0 100 100">
                                            {/* Base Track */}
                                            <circle cx="50" cy="50" r="38" fill="none" strokeWidth="11" className="donut-track"/>
                                            {/* Exact Segment */}
                                            <circle cx="50" cy="50" r="38" fill="none" stroke="#16A34A" strokeWidth="11"
                                                strokeDasharray={`${exactLength} 238.76`} strokeDashoffset="0" strokeLinecap="butt"/>
                                            {/* Approximate Segment */}
                                            <circle cx="50" cy="50" r="38" fill="none" stroke="#D97706" strokeWidth="11"
                                                strokeDasharray={`${approxLength} 238.76`} strokeDashoffset={`-${exactLength}`} strokeLinecap="butt"/>
                                            {/* Failed Segment */}
                                            <circle cx="50" cy="50" r="38" fill="none" stroke="#DC2626" strokeWidth="11"
                                                strokeDasharray={`${failedLength} 238.76`} strokeDashoffset={`-${exactLength + approxLength}`} strokeLinecap="butt"/>
                                            {/* Pending Segment */}
                                            <circle cx="50" cy="50" r="38" fill="none" stroke="#AEAEB2" strokeWidth="11"
                                                strokeDasharray={`${pendingLength} 238.76`} strokeDashoffset={`-${exactLength + approxLength + failedLength}`} strokeLinecap="butt"/>
                                        </svg>
                                        <div className="donut-center">
                                            <div className="donut-value">{stats ? stats.total : '0'}</div>
                                            <div className="donut-label">Companies</div>
                                        </div>
                                    </div>

                                    <div className="quality-legend-modern">
                                        {/* Row 1: Exact */}
                                        <div className="legend-row-modern">
                                            <div className="legend-header-modern">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div className="legend-swatch-modern" style={{ background: '#16A34A' }}></div>
                                                    <span className="legend-title-modern">Exakte Position</span>
                                                </div>
                                                <div className="legend-value-modern">
                                                    <strong>{stats ? stats.exact : '0'}</strong>
                                                    <span className="legend-value-sep-modern">/</span>
                                                    <span className="legend-value-pct-modern">{exactPct}%</span>
                                                </div>
                                            </div>
                                            <div className="legend-desc-modern">Vollständige Adresse mit Straße und Hausnummer erkannt.</div>
                                            <div className="legend-progress-track">
                                                <div className="legend-progress-bar" style={{ width: `${exactPct}%`, background: '#16A34A' }}></div>
                                            </div>
                                        </div>

                                        {/* Row 2: Approx */}
                                        <div className="legend-row-modern">
                                            <div className="legend-header-modern">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div className="legend-swatch-modern" style={{ background: '#D97706' }}></div>
                                                    <span className="legend-title-modern">Ungefähre Position</span>
                                                </div>
                                                <div className="legend-value-modern">
                                                    <strong>{stats ? stats.approx : '0'}</strong>
                                                    <span className="legend-value-sep-modern">/</span>
                                                    <span className="legend-value-pct-modern">{approxPct}%</span>
                                                </div>
                                            </div>
                                            <div className="legend-desc-modern">Nur PLZ oder Stadt vorhanden — Pin liegt im Stadtgebiet.</div>
                                            <div className="legend-progress-track">
                                                <div className="legend-progress-bar" style={{ width: `${approxPct}%`, background: '#D97706' }}></div>
                                            </div>
                                        </div>

                                        {/* Row 3: Failed */}
                                        <div className="legend-row-modern">
                                            <div className="legend-header-modern">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div className="legend-swatch-modern" style={{ background: '#DC2626' }}></div>
                                                    <span className="legend-title-modern">Nicht geokodierbar</span>
                                                </div>
                                                <div className="legend-value-modern">
                                                    <strong>{stats ? stats.failed : '0'}</strong>
                                                    <span className="legend-value-sep-modern">/</span>
                                                    <span className="legend-value-pct-modern">{failedPct}%</span>
                                                </div>
                                            </div>
                                            <div className="legend-desc-modern">Fehlende oder fehlerhafte Adressdaten in HubSpot.</div>
                                            <div className="legend-progress-track">
                                                <div className="legend-progress-bar" style={{ width: `${failedPct}%`, background: '#DC2626' }}></div>
                                            </div>
                                        </div>

                                        {/* Row 4: Pending */}
                                        <div className="legend-row-modern">
                                            <div className="legend-header-modern">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div className="legend-swatch-modern" style={{ background: '#AEAEB2' }}></div>
                                                    <span className="legend-title-modern">Noch zu geokodieren</span>
                                                </div>
                                                <div className="legend-value-modern">
                                                    <strong>{stats ? stats.pending : '0'}</strong>
                                                    <span className="legend-value-sep-modern">/</span>
                                                    <span className="legend-value-pct-modern">{pendingPct}%</span>
                                                </div>
                                            </div>
                                            <div className="legend-desc-modern">Gefundene B2B-Standorte, die noch geokodiert werden müssen.</div>
                                            <div className="legend-progress-track">
                                                <div className="legend-progress-bar" style={{ width: `${pendingPct}%`, background: '#AEAEB2' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="setting-group" style={{ marginTop: '32px' }}>
                                <div className="group-title">Daten-Synchronisierung & Rematch</div>
                                
                                <div className="sync-grid">
                                    {/* Card 1: Onboarding Flow Link */}
                                    <div className="sync-card">
                                        <div className="sync-card-icon-wrapper">
                                            <Database size={20} className="sync-card-icon" />
                                        </div>
                                        <div className="sync-card-content">
                                            <h3 className="sync-card-title">Geführte Synchronisierung</h3>
                                            <p className="sync-card-desc">
                                                Starte den Onboarding-Prozess neu, um neue HubSpot-Eigenschaften anzulegen, deine Datensätze zu analysieren und alle Unternehmen neu zu geokodieren.
                                            </p>
                                            <a 
                                                href={`/welcome?portalId=${portalId}`} 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-ghost sync-card-btn"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', width: 'fit-content' }}
                                            >
                                                Onboarding-Flow öffnen <ChevronRight size={14} />
                                            </a>
                                        </div>
                                    </div>

                                    {/* Card 2: Rematch-Center */}
                                    <div className="sync-card">
                                        <div className="sync-card-icon-wrapper">
                                            <RefreshCw size={20} className="sync-card-icon" />
                                        </div>
                                        <div className="sync-card-content">
                                            <h3 className="sync-card-title">Rematch & Korrekturen</h3>
                                            <p className="sync-card-desc">
                                                Setze geokodierte Koordinaten in HubSpot zurück, um bei der nächsten Synchronisierung einen neuen Geocoding-Versuch zu starten (z. B. nach Adresskorrekturen).
                                            </p>
                                            
                                            <div className="sync-action-buttons">
                                                <button 
                                                    className="btn btn-ghost" 
                                                    onClick={() => handleRematch('failed')}
                                                    disabled={isRematching !== null || isSyncing || !stats?.failed}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px' }}
                                                >
                                                    {isRematching === 'failed' ? <RefreshCw className="loader" size={13} /> : <Trash2 size={13} />}
                                                    Fehlgeschlagene zurücksetzen ({stats ? stats.failed : 0})
                                                </button>
                                                
                                                <button 
                                                    className="btn btn-ghost danger-ghost" 
                                                    onClick={() => {
                                                        if (window.confirm("Bist du sicher, dass du alle berechneten Standorte zurücksetzen möchtest? Dies erfordert eine komplette Neu-Geocodierung aller Adressen.")) {
                                                            handleRematch('all');
                                                        }
                                                    }}
                                                    disabled={isRematching !== null || isSyncing || !stats?.deTotal}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px' }}
                                                >
                                                    {isRematching === 'all' ? <RefreshCw className="loader" size={13} /> : <RefreshCw size={13} />}
                                                    Komplettes Rematch ({stats ? stats.deTotal : 0})
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div className="setting-group">
                                <div className="group-title">Anzeige</div>
                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-label">Ungefähre Pins in Karte anzeigen</div>
                                        <div className="setting-desc">Wenn deaktiviert, werden nur exakt geokodierte Pins angezeigt. Verhindert irreführende Treffer bei Umkreis-Suchen.</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className={`toggle ${showApproxPins ? 'on' : ''}`} onClick={() => setShowApproxPins(!showApproxPins)}></div>
                                        <span style={{ fontSize: '12.5px', color: 'var(--ink-mute)' }}>{showApproxPins ? 'Aktiviert' : 'Deaktiviert'}</span>
                                    </div>
                                </div>
                            </div>

                        </section>
                    )}

                    {/* ============ TAB: SUCHFILTER ============ */}
                    {activeTab === 'filters' && (
                        <section className="section active">
                            <div className="page-header">
                                <div className="eyebrow">Filter & Karte</div>
                                <h1 className="page-title">Suchfilter</h1>
                                <p className="page-lead">Verwalte gespeicherte Filter-Presets, die dein Team direkt aus der Karte heraus laden kann.</p>
                            </div>

                            <div className="setting-group">
                                <div className="group-title">Verfügbare Filter-Properties</div>
                                <p style={{ fontSize: '13px', color: 'var(--ink-mute)', margin: '-4px 0 16px', maxWidth: '620px', lineHeight: '1.55' }}>
                                    Diese HubSpot-Properties stehen deinem Team bei der Karten-Suche als Filter zur Verfügung. Per Drag &amp; Drop sortieren — die Reihenfolge bestimmt, wie die Filter im Filter-Panel angezeigt werden. Maximal 7 Properties.
                                </p>

                                <div className="property-counter">
                                    <div className="property-counter-text"><strong>{activeFilters.length}</strong> von <strong>7</strong> Properties aktiv</div>
                                    <div className="property-counter-bar">
                                        {[...Array(7)].map((_, i) => (
                                            <div key={i} className={`property-counter-dot ${i < activeFilters.length ? (activeFilters.length === 7 ? 'full' : 'filled') : ''}`} />
                                        ))}
                                    </div>
                                </div>

                                <div className="property-list">
                                    {activeFilters.map((f, idx) => {
                                        const fname = f.name || f;
                                        const flabel = f.label || f;
                                        const isCustom = !fname.startsWith('hs_') && !['annualrevenue', 'numberofemployees', 'hubspot_owner_id', 'hubspot_team_id'].includes(fname);
                                        const isDragging = draggedIndex === idx;

                                        return (
                                            <div 
                                                key={fname} 
                                                className={`property-row ${isDragging ? 'dragging' : ''}`}
                                                draggable
                                                onDragStart={() => handleDragStart(idx)}
                                                onDragEnter={() => handleDragEnter(idx)}
                                                onDragEnd={handleDragEnd}
                                                onDragOver={(e) => e.preventDefault()}
                                            >
                                                <div className="property-drag">
                                                    <GripVertical size={16}/>
                                                </div>
                                                <div className="property-name">{flabel}</div>
                                                <div className={`property-type ${isCustom ? 'custom' : 'standard'}`}>
                                                    {isCustom ? 'Custom' : 'Standard'}
                                                </div>
                                                <button className="property-remove" title="Entfernen" onClick={() => removeFilter(fname)}>
                                                    <X size={14}/>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {!showAddFilter ? (
                                    <button className="property-add-row" onClick={() => setShowAddFilter(true)} disabled={activeFilters.length >= 7}>
                                        <Plus size={16}/> Property hinzufügen
                                    </button>
                                ) : (
                                    <div style={{ background: 'var(--bg-soft)', borderRadius: '10px', padding: '16px', border: '1px solid var(--line-strong)', marginTop: '8px' }}>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                            <div className="input-group" style={{ flex: 1 }}>
                                                <input className="input" type="text" placeholder="Suche..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}/>
                                                <div className="input-suffix"><Search size={14}/></div>
                                            </div>
                                            <button className="btn btn-ghost" onClick={() => { setShowAddFilter(false); setFilterSearch(''); }}>Abbrechen</button>
                                        </div>
                                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--line)' }}>
                                            {properties
                                                .filter(p => !activeFilters.some(f => f.name === p.name))
                                                .filter(p => p.label.toLowerCase().includes(filterSearch.toLowerCase()) || p.name.toLowerCase().includes(filterSearch.toLowerCase()))
                                                .slice(0, 30)
                                                .map(p => (
                                                    <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
                                                        <div>
                                                            <div style={{ fontWeight: '600', fontSize: '13px' }}>{p.label}</div>
                                                            <div style={{ fontSize: '11px', color: 'var(--ink-mute)', fontFamily: 'var(--mono)' }}>{p.name}</div>
                                                        </div>
                                                        <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => addFilter(p)}>Hinzufügen</button>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="setting-group">
                                <div className="group-title">Gespeicherte Filter-Presets</div>

                                <div className={`preset-master ${filterPresetsEnabled ? 'on' : ''}`}>
                                    <div className="preset-master-meta">
                                        <div className="preset-master-title">Filter-Presets verwenden</div>
                                        <div className="preset-master-desc">Wenn aktiviert, sehen deine User in der Karten-Suche ein Dropdown mit den unten konfigurierten Presets.</div>
                                    </div>
                                    <div className={`toggle toggle-lg ${filterPresetsEnabled ? 'on' : ''}`} onClick={() => setFilterPresetsEnabled(!filterPresetsEnabled)}></div>
                                </div>

                                <div className={`preset-collapse ${!filterPresetsEnabled ? 'collapsed' : ''}`}>
                                    {filterPresets.map(preset => (
                                        <div key={preset.id} className="preset-card">
                                            <div className="preset-icon">
                                                <Filter size={16}/>
                                            </div>
                                            <div className="preset-meta">
                                                <div className="preset-name">{preset.name}</div>
                                                <div className="preset-desc" style={{ color: 'var(--ink-mute)', fontSize: '12.5px' }}>{preset.desc}</div>
                                            </div>
                                            <div className="preset-actions">
                                                <button className="icon-btn" onClick={() => {
                                                    setEditingPreset(preset);
                                                    setPresetNameInput(preset.name);
                                                    setPresetRules(preset.rules || []);
                                                }}>
                                                    <Edit2 size={13}/>
                                                </button>
                                                <button className="icon-btn" onClick={() => deletePreset(preset.id)}>
                                                    <Trash2 size={13}/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Edit / Create Form */}
                                    <div style={{ background: 'var(--bg-soft)', borderRadius: '12px', padding: '16px', border: '1px solid var(--line)', marginTop: '14px' }}>
                                        <div className="sub-title">{editingPreset ? 'Preset bearbeiten' : 'Neues Preset anlegen'}</div>
                                        <div style={{ display: 'grid', gap: '12px', marginTop: '10px' }}>
                                            <div>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--ink-soft)' }}>Preset-Name</label>
                                                <input className="input" style={{ marginTop: '4px' }} type="text" value={presetNameInput} onChange={(e) => setPresetNameInput(e.target.value)} placeholder="z.B. Tech-Unternehmen NRW" />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '12.5px', fontWeight: '600', color: 'var(--ink-soft)', display: 'block', marginBottom: '6px' }}>Filterkriterien (UND-Verknüpfung)</label>
                                                
                                                <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                                                    {presetRules.map((rule, idx) => {
                                                        const prop = properties.find(p => p.name === rule.property) || properties[0] || { name: '', label: '', type: 'string' };
                                                        const pType = prop.type || 'string';
                                                        const hasOptions = prop.options && Array.isArray(prop.options) && prop.options.length > 0;
                                                        
                                                        return (
                                                            <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', background: 'var(--bg)', padding: '8px', borderRadius: '8px', border: '1px solid var(--line)', flexWrap: 'wrap' }}>
                                                                {/* Property Select */}
                                                                <select 
                                                                    className="select" 
                                                                    style={{ flex: 2, minWidth: '135px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--line)' }} 
                                                                    value={rule.property} 
                                                                    onChange={(e) => {
                                                                        const selectedPropName = e.target.value;
                                                                        const selectedProp = properties.find(p => p.name === selectedPropName);
                                                                        const defaultOp = (selectedProp && selectedProp.type === 'number') ? 'GT' : 'EQ';
                                                                        updatePresetRule(idx, { property: selectedPropName, operator: defaultOp, value: '' });
                                                                    }}
                                                                >
                                                                    {properties.map(p => (
                                                                        <option key={p.name} value={p.name}>{p.label || p.name}</option>
                                                                    ))}
                                                                </select>

                                                                {/* Operator Select */}
                                                                <select 
                                                                    className="select" 
                                                                    style={{ flex: 1.2, minWidth: '95px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--line)' }} 
                                                                    value={rule.operator} 
                                                                    onChange={(e) => updatePresetRule(idx, { ...rule, operator: e.target.value })}
                                                                >
                                                                    <option value="EQ">ist</option>
                                                                    <option value="NEQ">ist nicht</option>
                                                                    {pType !== 'number' && pType !== 'enumeration' && <option value="CONTAINS">enthält</option>}
                                                                    {pType === 'number' && <option value="GT">&gt; (mehr)</option>}
                                                                    {pType === 'number' && <option value="LT">&lt; (weniger)</option>}
                                                                    <option value="HAS_PROPERTY">bekannt</option>
                                                                    <option value="NOT_HAS_PROPERTY">unbekannt</option>
                                                                </select>

                                                                {/* Value input / dropdown */}
                                                                {rule.operator !== 'HAS_PROPERTY' && rule.operator !== 'NOT_HAS_PROPERTY' && (
                                                                    hasOptions ? (
                                                                        <select 
                                                                            className="select" 
                                                                            style={{ flex: 2, minWidth: '120px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--line)' }} 
                                                                            value={rule.value} 
                                                                            onChange={(e) => updatePresetRule(idx, { ...rule, value: e.target.value })}
                                                                        >
                                                                            <option value="">-- Wert --</option>
                                                                            {prop.options.map(opt => (
                                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <input 
                                                                            className="input" 
                                                                            style={{ flex: 2, minWidth: '120px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--line)' }} 
                                                                            type={pType === 'number' ? 'number' : 'text'} 
                                                                            value={rule.value} 
                                                                            placeholder="Wert" 
                                                                            onChange={(e) => updatePresetRule(idx, { ...rule, value: e.target.value })}
                                                                        />
                                                                    )
                                                                )}

                                                                {/* Delete btn */}
                                                                <button 
                                                                    className="btn btn-ghost" 
                                                                    style={{ padding: '6px', minWidth: '32px', height: '32px', color: 'var(--ink-mute)' }}
                                                                    onClick={() => deletePresetRule(idx)}
                                                                >
                                                                    <Trash2 size={13}/>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <button 
                                                    className="btn btn-ghost" 
                                                    style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                    onClick={addPresetRule}
                                                >
                                                    <Plus size={12}/> Kriterium hinzufügen
                                                </button>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                                                {editingPreset && (
                                                    <button className="btn btn-ghost" onClick={() => {
                                                        setEditingPreset(null);
                                                        setPresetNameInput('');
                                                        setPresetRules([]);
                                                    }}>Abbrechen</button>
                                                )}
                                                <button className="btn btn-brand" onClick={handleAddOrEditPreset}>
                                                    {editingPreset ? 'Speichern' : 'Hinzufügen'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ============ TAB: APPEARANCE ============ */}
                    {activeTab === 'appearance' && (
                        <section className="section active">
                            <div className="page-header">
                                <div className="eyebrow">Karten-Darstellung</div>
                                <h1 className="page-title">Appearance</h1>
                                <p className="page-lead">Wie sich die Karte für dein Team präsentiert. Visualisierung und Animationen — gilt portalweit.</p>
                            </div>

                            <div className="setting-group">
                                <div className="group-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>Animation</span>
                                    <div style={{ position: 'relative', width: '20px', height: '20px', display: 'grid', placeItems: 'center' }}>
                                        <div className={`map-preview-pin ${pulsePinsEnabled || pulseOnlyActiveDeals ? 'pulse' : ''}`} style={{ position: 'relative', top: 'auto', left: 'auto', transform: 'none', width: '10px', height: '10px', background: '#007AFF', borderRadius: '50%' }}></div>
                                    </div>
                                </div>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-label">Pulsierende Pins</div>
                                        <div className="setting-desc">Pins bekommen einen leichten Pulse-Effekt. Hilft beim schnellen Erfassen vieler Punkte, lenkt aber bei Routenplanung ab.</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className={`toggle ${pulsePinsEnabled ? 'on' : ''}`} onClick={handleTogglePulsePins}></div>
                                        <span style={{ fontSize: '12.5px', color: 'var(--ink-mute)' }}>{pulsePinsEnabled ? 'Aktiviert' : 'Deaktiviert'}</span>
                                    </div>
                                </div>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-label">Pulse nur bei aktiven Deals</div>
                                        <div className="setting-desc">Nur Pins von Companies mit offenen Deals pulsieren. Lenkt den Blick auf die wichtigsten Standorte.</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className={`toggle ${pulseOnlyActiveDeals ? 'on' : ''}`} onClick={handleTogglePulseOnlyActiveDeals}></div>
                                        <span style={{ fontSize: '12.5px', color: 'var(--ink-mute)' }}>{pulseOnlyActiveDeals ? 'Aktiviert' : 'Deaktiviert'}</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ============ TAB: ZUGRIFF ============ */}
                    {activeTab === 'access' && (
                        <section className="section active">
                            <div className="page-header">
                                <div className="eyebrow">User & Rollen</div>
                                <h1 className="page-title">Zugriff</h1>
                                <p className="page-lead">DealPin synct automatisch mit deinen HubSpot-Usern. Hier legst du Rollen fest und siehst, wer DealPin wie oft nutzt.</p>
                            </div>

                            <div className="setting-group">
                                <div className="group-title">Allgemein</div>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-label">Automatischer Zugriff für neue HubSpot-User</div>
                                        <div className="setting-desc">Sobald ein neuer User in deinem HubSpot-Portal angelegt wird, bekommt er automatisch die Rolle <em>User</em>. Ist die Option aus, startet er mit <em>Kein Zugriff</em> und muss manuell freigeschaltet werden.</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div className={`toggle ${autoUserAccess ? 'on' : ''}`} onClick={() => setAutoUserAccess(!autoUserAccess)}></div>
                                        <span style={{ fontSize: '12.5px', color: 'var(--ink-mute)' }}>{autoUserAccess ? 'Aktiviert' : 'Deaktiviert'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="setting-group">
                                <div className="group-title">User <span style={{ color: 'var(--ink-faint)', fontWeight: '400' }}>· {users.length} aus HubSpot</span></div>
                                
                                <div style={{ background: 'var(--bg-soft)', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', border: '1px solid var(--line)' }}>
                                    <Search size={16} style={{ color: 'var(--ink-mute)' }}/>
                                    <input className="input" style={{ border: 'none', background: 'transparent', padding: '4px' }} type="text" placeholder="Suche..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)}/>
                                </div>

                                <div className="panel">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '38%' }}>
                                                    <span 
                                                        className={`sortable-link ${userSortField === 'name' ? 'active' : ''}`} 
                                                        onClick={() => handleSort('name')}
                                                    >
                                                        Name {userSortField === 'name' && (userSortDirection === 'asc' ? '▲' : '▼')}
                                                    </span>
                                                    <span className="sortable-link-sep">/</span>
                                                    <span 
                                                        className={`sortable-link ${userSortField === 'email' ? 'active' : ''}`} 
                                                        onClick={() => handleSort('email')}
                                                    >
                                                        Nutzer {userSortField === 'email' && (userSortDirection === 'asc' ? '▲' : '▼')}
                                                    </span>
                                                </th>
                                                <th style={{ width: '18%' }}>Rolle</th>
                                                <th 
                                                    style={{ width: '22%' }} 
                                                    className={`sortable ${userSortField === 'lastActive' ? 'active' : ''}`}
                                                    onClick={() => handleSort('lastActive')}
                                                >
                                                    Letzte Aktivität {userSortField === 'lastActive' && (userSortDirection === 'asc' ? '▲' : '▼')}
                                                </th>
                                                <th 
                                                    style={{ width: '22%' }} 
                                                    className={`sortable ${userSortField === 'usage' ? 'active' : ''}`}
                                                    onClick={() => handleSort('usage')}
                                                >
                                                    Nutzung <span style={{ color: 'var(--ink-faint)', textTransform: 'none', letterSpacing: 0, fontSize: '10px' }}>· 30 Tage</span> {userSortField === 'usage' && (userSortDirection === 'asc' ? '▲' : '▼')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users
                                                .filter(u => `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase()))
                                                .sort((a, b) => {
                                                    let cmp = 0;
                                                    if (userSortField === 'name') {
                                                        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
                                                        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
                                                        const strA = nameA || a.email || '';
                                                        const strB = nameB || b.email || '';
                                                        cmp = strA.localeCompare(strB, 'de', { numeric: true, sensitivity: 'base' });
                                                    } else if (userSortField === 'email') {
                                                        const strA = a.email || '';
                                                        const strB = b.email || '';
                                                        cmp = strA.localeCompare(strB, 'de', { numeric: true, sensitivity: 'base' });
                                                    } else if (userSortField === 'lastActive') {
                                                        const uIdA = a.userId ? String(a.userId) : String(a.id);
                                                        const uIdB = b.userId ? String(b.userId) : String(b.id);
                                                        const actA = userActivity[uIdA]?.lastActive;
                                                        const actB = userActivity[uIdB]?.lastActive;
                                                        const timeA = actA ? new Date(actA).getTime() : 0;
                                                        const timeB = actB ? new Date(actB).getTime() : 0;
                                                        cmp = timeA - timeB;
                                                    } else if (userSortField === 'usage') {
                                                        const uIdA = a.userId ? String(a.userId) : String(a.id);
                                                        const uIdB = b.userId ? String(b.userId) : String(b.id);
                                                        const useA = userActivity[uIdA]?.searchCount30d || 0;
                                                        const useB = userActivity[uIdB]?.searchCount30d || 0;
                                                        cmp = useA - useB;
                                                    }

                                                    if (cmp !== 0) {
                                                        return userSortDirection === 'asc' ? cmp : -cmp;
                                                    }

                                                    const idA = String(a.userId || a.id || '');
                                                    const idB = String(b.userId || b.id || '');
                                                    return idA.localeCompare(idB);
                                                })
                                                .map(user => {
                                                    const uIdStr = user.userId ? String(user.userId) : String(user.id);
                                                    const currentRole = userRoles[uIdStr] || (allowedUsers.includes(uIdStr) ? 'user' : (autoUserAccess ? 'user' : 'none'));
                                                    const activity = userActivity[uIdStr];
                                                    
                                                    // Parse activity
                                                    let actTime = '—';
                                                    if (activity && activity.lastActive) {
                                                        const date = new Date(activity.lastActive);
                                                        const diffMs = Date.now() - date.getTime();
                                                        const diffMins = Math.round(diffMs / 60000);
                                                        if (diffMins < 5) actTime = 'gerade aktiv';
                                                        else if (diffMins < 60) actTime = `vor ${diffMins} Min.`;
                                                        else {
                                                            const diffHrs = Math.round(diffMins / 60);
                                                            if (diffHrs < 24) actTime = `vor ${diffHrs} Std.`;
                                                            else actTime = date.toLocaleDateString();
                                                        }
                                                    }

                                                    const avatarName = `${user.firstName ? user.firstName[0] : ''}${user.lastName ? user.lastName[0] : ''}`.toUpperCase();

                                                    return (
                                                        <tr key={uIdStr}>
                                                            <td>
                                                                <div className="user-cell">
                                                                    <div className="avatar" style={{ background: user.lastName && user.lastName[0] > 'M' ? 'linear-gradient(135deg, #9333EA 0%, #DB2777 100%)' : 'linear-gradient(135deg, #2196E8 0%, #1E62D8 100%)' }}>
                                                                        {avatarName}
                                                                    </div>
                                                                    <div>
                                                                        <div className="user-name">{user.firstName} {user.lastName}</div>
                                                                        <div className="user-email">{user.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <select className={`role-select role-${currentRole}`} value={currentRole} onChange={(e) => handleRoleChange(uIdStr, e.target.value)}>
                                                                    <option value="admin">Admin</option>
                                                                    <option value="user">User</option>
                                                                    <option value="none">Kein Zugriff</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <span className={activity?.lastActive ? 'cell-mono' : 'cell-mono-mute'}>
                                                                    {activity?.lastActive && <span className="activity-dot"></span>}
                                                                    {actTime}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={activity?.searchCount30d ? 'cell-mono' : 'cell-mono-mute'}>
                                                                    {activity?.searchCount30d ? `${activity.searchCount30d} Suchen` : '—'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="sync-hint">
                                    <Info size={16}/>
                                    <div>
                                        User werden automatisch aus deinem HubSpot-Portal gesynct — neue Personen erscheinen hier, sobald sie in HubSpot angelegt sind. Um jemandem den Zugriff zu entziehen, setze die Rolle auf <strong>Kein Zugriff</strong>.
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}


                </div>
            </main>

            {/* Video Modal Backdrop */}
            {videoModal.open && (
                <div className="modal-backdrop open" onClick={() => setVideoModal({ ...videoModal, open: false })}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-video">
                            <button className="modal-close" onClick={() => setVideoModal({ ...videoModal, open: false })}>
                                <X size={16}/>
                            </button>
                            <div className="modal-video-placeholder">
                                <Play size={48} style={{ display: 'block', margin: '0 auto 12px' }}/>
                                Video-Player ({videoModal.title})
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className="modal-tag">{videoModal.tag}</div>
                            <div className="modal-title">{videoModal.title}</div>
                            <div className="modal-desc">{videoModal.desc}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
